/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 *
 */

/**
 * Factory object for multiple classes. You can use any string to register class to the container.
 *
 * @constructor
 */
import {logInfo} from "App/debug";
import Container from "App/utils/container";
import {AbstractRenderGraph} from "App/render/abstract_render_graph";
import IScene from "js_interface/src/scene_interface";

export enum Containers {
    SHADERS = 'shaders',
    SHADERS_SM_VSM= 'shaders_sm_vsm',
    SHADERS_SM_PCF= 'shaders_sm_pcf',
    SHADERS_SSAO= 'shaders_ssao',
    SHADERS_HUD= 'shaders_hud',
    SHADERS_ENVIRONMENT= 'shaders_env',

    PASSES= 'passes',
    PASSES_SM= 'passes_sm',
    PASSES_SSAO= 'passes_ssao',
    PASSES_HUD= 'passes_hud',
    PASSES_CONTROLS= 'passes_controls',
    PASSES_ENVIRONMENT= 'passes_env',

    BINS= 'bins'
}

export class Factory {
    private static containers: {} = {};

    constructor() {
    }

    /**
     * Register object to specific container
     *
     * @param {string} aContainer
     * @param {object} aClass
     */
    public static register(aContainer: string, aClass: any): void {
        if (Factory.containers[aContainer] === undefined) {
            Factory.containers[aContainer] = new Container();
        }

        Factory.containers[aContainer].add(aClass.TypeName, aClass);
    }

    /**
     * Unregister object from specific container
     *
     * @param {string} aContainer
     * @param {object} aClass
     * @return {boolean}
     */
    public static unregister(aContainer: string, aClass: any): boolean {
        let container = Factory.containers[aContainer];
        if (container === undefined) {
            return false;
        }

        return container.remove(aClass.TypeName);
    }

    /**
     * Create new instance from registered class in container
     *
     * @param {string} aContainer
     * @param {object} aClass
     * @param {cadwork.webgl.SceneAPI.Scene} aSceneAPI
     * @param {cadwork.webgl.render.AbstractRenderGraph} aRenderGraph
     * @return {object | null}
     */
    public static create(aContainer: string, aClass: any, aSceneAPI: IScene, aRenderGraph: AbstractRenderGraph): any {
        let container = Factory.containers[aContainer];
        if (container === undefined) {
            logInfo("[Factory] Nonexistent container: " + aContainer);
            return null;
        }

        let creator = container.get(aClass.TypeName);
        if (creator === undefined) {
            logInfo("[Factory] Nonexistent class: " + aClass.TypeName + " in " + aContainer);
            return null;
        }

        let instance = creator.create(aSceneAPI, aRenderGraph);
        instance._typeName = aClass.TypeName;
        return instance;
    }

    /**
     * Create new instances from registered classes in container
     *
     * @param {string} aContainer
     * @param {cadwork.webgl.SceneAPI.Scene} aSceneAPI
     * @param {cadwork.webgl.render.AbstractRenderGraph} aRenderGraph
     * @return {*}
     */
    public static createAll(aContainer: string, aSceneAPI: IScene, aRenderGraph: AbstractRenderGraph): {} {
        let container = Factory.containers[aContainer];
        if (container === undefined) {
            logInfo("[Factory] Nonexistent container: " + aContainer);
            return null;
        }

        let creators = container.getObjects();
        let instances = {};
        for (let key in creators) {
            if (!creators.hasOwnProperty(key)) {
                continue;
            }

            let cc = creators[key];
            let instance = cc.create(aSceneAPI, aRenderGraph);
            instance._typeName = cc.TypeName;
            instances[key] = instance;
        }

        return instances;
    }
}
